import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Plus, Monitor, Tablet, Smartphone, Search, X } from 'lucide-react';
import { fetchThemes, createTheme, duplicateTheme, deleteTheme, invalidateThemesCache, markThemeAsDefault } from '../../services/api';
import { ConfirmModal, FormModal } from '../common/Modal';
import { paths } from '../../routes';
import Header from '../Header';
import ThemeCard from './ThemeCard';
import { showToast } from '../../redux/store';

/* ─── ThemeListPage ─────────────────────────────────────────────────────────── */

const ThemeListPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newThemeName, setNewThemeName] = useState('');
    const [newThemeDevice, setNewThemeDevice] = useState('desktop');
    const [isCreating, setIsCreating] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [search, setSearch] = useState(() => localStorage.getItem('themes-search') || '');

    const [activeDevice, setActiveDevice] = useState(() => {
        const saved = localStorage.getItem('themes-active-device');
        return ['desktop', 'tablet', 'mobile'].includes(saved) ? saved : 'desktop';
    });

    const handleDeviceChange = (d) => {
        setActiveDevice(d);
        localStorage.setItem('themes-active-device', d);
    };

    const handleSearchChange = (v) => {
        setSearch(v);
        localStorage.setItem('themes-search', v);
    };

    useEffect(() => {
        invalidateThemesCache();
        setLoading(true);
        fetchThemes({})
            .then((data) => setThemes(data || []))
            .catch((e) => console.error('Failed to load themes', e))
            .finally(() => setLoading(false));
    }, []);

    const loadThemes = async () => {
        setLoading(true);
        try {
            const data = await fetchThemes({});
            setThemes(data || []);
        } catch (e) {
            console.error('Failed to load themes', e);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!newThemeName.trim()) return;
        setIsCreating(true);
        try {
            const newTheme = await createTheme({ name: newThemeName.trim(), device_type: newThemeDevice });
            setShowCreateModal(false);
            setNewThemeName('');
            setNewThemeDevice('desktop');
            navigate(paths.themeEditor(newTheme.id));
        } catch (e) {
            console.error('Failed to create theme', e);
            dispatch(showToast({ message: 'Failed to create theme', type: 'error' }));
        } finally {
            setIsCreating(false);
        }
    };

    const handleEdit = (theme) => {
        navigate(paths.themeEditor(theme.id));
    };

    const handleDuplicate = async (themeId) => {
        try {
            await duplicateTheme(themeId);
            await loadThemes();
            dispatch(showToast({ message: 'Theme duplicated successfully', type: 'success' }));
        } catch (e) {
            console.error('Failed to duplicate theme', e);
            dispatch(showToast({ message: 'Failed to duplicate theme', type: 'error' }));
        }
    };

    const handleMakeDefault = async (theme) => {
        try {
            await markThemeAsDefault(theme.id);
            await loadThemes();
            dispatch(showToast({ message: `${theme.name} is now the platform default for ${theme.device_type}`, type: 'success' }));
        } catch (e) {
            dispatch(showToast({ message: e.response?.data?.error || e.message, type: 'error' }));
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await deleteTheme(deleteTarget.id);
            setDeleteTarget(null);
            await loadThemes();
            dispatch(showToast({ message: 'Theme deleted successfully', type: 'success' }));
        } catch (e) {
            const status = e?.response?.status;
            if (status === 403) {
                dispatch(showToast({ message: 'Cannot delete built-in theme', type: 'error' }));
            } else if (status === 409) {
                const appNames =
                    e?.response?.data?.app_names?.join(', ') ||
                    e?.response?.data?.message ||
                    'one or more apps';
                dispatch(showToast({ message: `Theme is in use by ${appNames}`, type: 'error' }));
            } else {
                dispatch(showToast({ message: 'Failed to delete theme', type: 'error' }));
            }
            setDeleteTarget(null);
        } finally {
            setIsDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-200 dark:border-indigo-900 border-t-indigo-600 rounded-full animate-spin" />
                <p className="mt-4 text-slate-500 dark:text-slate-400 font-bold animate-pulse">Loading Themes...</p>
            </div>
        );
    }

    const query = search.trim().toLowerCase();
    const deviceThemes = themes.filter(t => t.device_type === activeDevice);
    const visibleThemes = deviceThemes
        .filter(t => {
            if (!query) return true;
            return (
                (t.name || '').toLowerCase().includes(query) ||
                (t.description || '').toLowerCase().includes(query) ||
                (t.theme_key || '').toLowerCase().includes(query)
            );
        })
        // Platform default floats to the top of its device group.
        .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col">
            <Header />

            <main className="flex-1 p-8 max-w-7xl mx-auto w-full">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-200 tracking-tight">Theme Management</h1>
                        <p className="text-slate-500 dark:text-slate-400 mt-1">Manage and customize themes for your applications.</p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-bold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
                    >
                        <Plus size={20} strokeWidth={2} className="-ml-1 mr-2" />
                        Create Theme
                    </button>
                </div>

                {/* Device filter tabs + search */}
                <div className="flex items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-700 mb-4">
                    <div className="flex items-center gap-1">
                        {[
                            { value: 'desktop', label: 'Desktop', activeClass: 'border-indigo-500 text-indigo-600 dark:text-indigo-400' },
                            { value: 'tablet',  label: 'Tablet',  activeClass: 'border-violet-500 text-violet-600 dark:text-violet-400'  },
                            { value: 'mobile',  label: 'Mobile',  activeClass: 'border-emerald-500 text-emerald-600 dark:text-emerald-400' },
                        ].map(t => (
                            <button
                                key={t.value}
                                onClick={() => handleDeviceChange(t.value)}
                                className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                                    activeDevice === t.value
                                        ? t.activeClass
                                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="relative mb-2 w-full max-w-xs">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder="Search themes..."
                            className="w-full pl-9 pr-8 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-colors"
                        />
                        {search && (
                            <button
                                onClick={() => handleSearchChange('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                                title="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Result count */}
                <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
                    {query
                        ? `${visibleThemes.length} of ${deviceThemes.length} ${activeDevice} ${deviceThemes.length === 1 ? 'theme' : 'themes'} match`
                        : `${deviceThemes.length} ${activeDevice} ${deviceThemes.length === 1 ? 'theme' : 'themes'}`}
                </p>

                {/* Theme grid — filtered by active device */}
                {visibleThemes.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                        {query ? (
                            <>
                                <p className="text-sm text-slate-400">No {activeDevice} themes match "{search.trim()}".</p>
                                <p className="text-xs text-slate-300 dark:text-slate-600 mt-1">Try a different search term.</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-slate-400">No themes for {activeDevice} yet.</p>
                                <p className="text-xs text-slate-300 dark:text-slate-600 mt-1">Click "Create Theme" to add one.</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {visibleThemes.map(theme => (
                            <ThemeCard
                                key={theme.id}
                                theme={theme}
                                onEdit={handleEdit}
                                onDuplicate={handleDuplicate}
                                onDelete={setDeleteTarget}
                                onMakeDefault={handleMakeDefault}
                            />
                        ))}
                    </div>
                )}
            </main>

            {/* Create Theme Modal */}
            <FormModal
                isOpen={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    setNewThemeName('');
                    setNewThemeDevice('desktop');
                }}
                onSubmit={handleCreate}
                title="Create Theme"
                subtitle="Give your new theme a name to get started."
                submitText="Create"
                isLoading={isCreating}
                submitDisabled={!newThemeName.trim()}
                size="sm"
            >
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide">
                            Theme Name
                        </label>
                        <input
                            type="text"
                            value={newThemeName}
                            onChange={(e) => setNewThemeName(e.target.value)}
                            placeholder="e.g. Corporate Blue"
                            autoFocus
                            className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 placeholder-slate-300 dark:placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Device</label>
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                { key: 'desktop', label: 'Desktop', Icon: Monitor    },
                                { key: 'tablet',  label: 'Tablet',  Icon: Tablet     },
                                { key: 'mobile',  label: 'Mobile',  Icon: Smartphone },
                            ].map(({ key, label, Icon }) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setNewThemeDevice(key)}
                                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1.5 ${
                                        newThemeDevice === key
                                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400'
                                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 text-slate-600 dark:text-slate-400'
                                    }`}
                                >
                                    <Icon size={20} strokeWidth={2} />
                                    <span className="text-xs font-medium">{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                title="Delete Theme?"
                message={
                    deleteTarget
                        ? `Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`
                        : ''
                }
                confirmText="Delete"
                confirmVariant="danger"
                isLoading={isDeleting}
                size="sm"
            />

        </div>
    );
};

export default ThemeListPage;
